/**
 * useSidebarBadges — live counts for sidebar nav badges.
 * Ported from school-transport-platform 2026-06-05; extended 2026-06-10
 * with messages, failures, inspections, duty-log and a Help "what's new" badge.
 *
 * Backend counts poll every 30s and fall back to 0 on any failure (including a
 * 403 for users without the relevant permission). The Help badge is derived
 * from CHANGELOG.md compared against a localStorage marker, and clears the
 * moment the user opens /pomoc.
 */
import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import { api } from '@/shared/lib/api';
import { wyczyscZnacznikiLicznikow } from '@/shared/lib/badge-ack';
import { useAuthStore } from '@store/auth.store';
import { useOverdueCounts } from '@/features/duty-log/instruktorzy/wspolne';

function num(v: any): number {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

const HELP_SEEN_KEY = 'zuris-helpLastSeenEntry';

export function useSidebarBadges() {
  /** Unread notifications */
  const notifs = useQuery({
    queryKey: ['sidebar', 'unread-notifications'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/notifications/count');
        return num(r?.count ?? r?.data?.count ?? 0);
      } catch { return 0; }
    },
    refetchInterval: 15_000,
    refetchOnWindowFocus: 'always',
    staleTime: 8_000,
  });

  /** Open incidents (status = OPEN) */
  const incidents = useQuery({
    queryKey: ['sidebar', 'open-incidents'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/incidents/stats');
        const s = r?.data ?? r ?? {};
        // byStatus is an array of { status, _count: { _all } } (Prisma groupBy)
        const bs = s?.byStatus;
        if (Array.isArray(bs)) {
          return num(bs.find((x: any) => x?.status === 'OPEN')?._count?._all ?? 0);
        }
        return num(bs?.OPEN ?? s?.open ?? 0);
      } catch { return 0; }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  /** Open feedback tickets (scoped to caller's role) */
  const feedback = useQuery({
    queryKey: ['sidebar', 'feedback-count'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/feedback/stats/mine-count');
        return num(r?.count ?? r?.data?.count ?? 0);
      } catch { return 0; }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  /** New board posts (Tablica) since the caller's last visit */
  const board = useQuery({
    queryKey: ['sidebar', 'board-count'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/board/unread-count');
        return num(r?.count ?? r?.data?.count ?? 0);
      } catch { return 0; }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  /** B-13 E4: zmienione kursy w Planie służb (GAiT) od ostatniego „Oznacz jako przeczytane” */
  const dutyPlan = useQuery({
    queryKey: ['sidebar', 'duty-plan-unseen'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/task-assignments/plan/unseen-count');
        return num(r?.count ?? r?.data?.count ?? 0);
      } catch { return 0; }
    },
    refetchInterval: 60_000,
    staleTime: 30_000,
  });

  /** Unread messages — sum unreadCount across conversations */
  const messages = useQuery({
    queryKey: ['sidebar', 'unread-messages'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/notifications/messages/conversations');
        const arr: any[] = Array.isArray(r) ? r : (r?.data ?? r?.items ?? []);
        return arr.reduce((sum, c) => sum + num(c?.unreadCount), 0);
      } catch { return 0; }
    },
    refetchInterval: 15_000,
    refetchOnWindowFocus: 'always',
    staleTime: 8_000,
  });

  /** Open failures */
  const failures = useQuery({
    queryKey: ['sidebar', 'open-failures'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/failures/stats');
        const s = r?.data ?? r ?? {};
        return num(s?.counts?.open ?? s?.open ?? 0);
      } catch { return 0; }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  /** Inspections needing attention — overdue + due-soon */
  const inspections = useQuery({
    queryKey: ['sidebar', 'inspections-due'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/inspections/stats');
        const s = r?.data ?? r ?? {};
        const c = s?.counts ?? s;
        return num(c?.overdue) + num(c?.dueSoon);
      } catch { return 0; }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  /** Forms needing attention — submitted (+ returned) awaiting action */
  const forms = useQuery({
    queryKey: ['sidebar', 'forms-attention'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/forms/stats');
        const s = r?.data ?? r ?? {};
        const c = s?.counts ?? s;
        return num(c?.attention ?? (num(c?.submitted) + num(c?.returned)));
      } catch { return 0; }
    },
    refetchInterval: 15_000,
    refetchOnWindowFocus: 'always',
    staleTime: 8_000,
  });

  /** Open duty-log entries */
  const dutyLog = useQuery({
    queryKey: ['sidebar', 'duty-log-open'],
    queryFn: async () => {
      try {
        const r: any = await api.get('/duty-log/stats');
        const s = r?.data ?? r ?? {};
        return num(s?.entriesOpen ?? 0);
      } catch { return 0; }
    },
    refetchInterval: 30_000,
    staleTime: 15_000,
  });

  /** B-652 — zaległości instruktorów (tylko ADMIN/DISPATCHER; ten sam klucz co licznik
   *  na zakładce „Instruktorzy”, więc jedno żądanie na oba miejsca). */
  const role = useAuthStore((s) => s.user?.role);
  const overdueQ = useOverdueCounts(role === 'ADMIN' || role === 'DISPATCHER');
  const instrUrgent = overdueQ.data?.urgent ?? 0;
  const instrOverdue = overdueQ.data?.overdue ?? 0;

  /** Help "what's new" — changelog entries newer than the last visit to /pomoc */
  const location = useLocation();
  const headersRef = useRef<string[]>([]);
  const [help, setHelp] = useState(0);

  const recomputeHelp = () => {
    const headers = headersRef.current;
    if (!headers.length) { setHelp(0); return; }
    let seen: string | null = null;
    try { seen = window.localStorage.getItem(HELP_SEEN_KEY); } catch { /* ignore */ }
    if (!seen) { setHelp(headers.length); return; }
    const idx = headers.indexOf(seen);
    // entries above the last-seen one are the new ones
    setHelp(idx === -1 ? headers.length : idx);
  };

  // Load changelog entry headers once (each "## ..." line is one release entry).
  useEffect(() => {
    let cancelled = false;
    fetch('/docs/CHANGELOG.md', { cache: 'no-store' })
      .then(r => (r.ok ? r.text() : ''))
      .then(txt => {
        if (cancelled) return;
        headersRef.current = (txt.match(/^## .+$/gm) ?? []).map(h => h.trim());
        recomputeHelp();
      })
      .catch(() => { /* ignore */ });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Mark the latest entry as seen the moment the user opens the Help page.
  useEffect(() => {
    if (location.pathname.startsWith('/pomoc') && headersRef.current.length) {
      try { window.localStorage.setItem(HELP_SEEN_KEY, headersRef.current[0]); } catch { /* ignore */ }
      setHelp(0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // B-307: jednorazowe sprzątnięcie znaczników po usuniętym mechanizmie wyciszania.
  useEffect(() => { wyczyscZnacznikiLicznikow(); }, []);

  return {
    notifs:      notifs.data      ?? 0,
    feedback:    feedback.data    ?? 0,
    board:       board.data       ?? 0,
    dutyPlan:    dutyPlan.data    ?? 0,
    messages:    messages.data    ?? 0,
    help,
    /* B-307: liczniki operacyjne BEZ wyciszania — pokazują stan faktyczny.
       To liczby pozycji OTWARTYCH, a nie nieprzeczytanych, więc nie ma czego
       „odczytywać"; schodzą, gdy pozycja zostanie zamknięta. Gdyby zostało tu
       dawne `effectiveBadge`, każdy, kto kiedykolwiek kliknął „Oznacz jako
       przeczytane", miałby licznik zaniżony NA ZAWSZE: znacznik leżał
       w `localStorage` jednej przeglądarki i nikt nie miał jak go podnieść.
       Stare wpisy sprzątamy przy starcie — patrz `badge-ack.ts`. */
    incidents:   incidents.data   ?? 0,
    failures:    failures.data    ?? 0,
    inspections: inspections.data ?? 0,
    /* B-652: przy „Dzienniku służby” pierwszeństwo mają zaległości instruktorów —
       pilne (czerwony), potem zaległe (bursztynowy); bez nich zostaje liczba otwartych
       wpisów jak dotąd. Ton czyta Sidebar z `dutyLogTone`. */
    dutyLog:     instrUrgent > 0 ? instrUrgent : instrOverdue > 0 ? instrOverdue : (dutyLog.data ?? 0),
    dutyLogTone: (instrUrgent > 0 ? 'red' : instrOverdue > 0 ? 'amber' : 'primary') as 'red' | 'amber' | 'primary',
    forms:       forms.data       ?? 0,
  };
}
